import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSnapshot } from "valtio";
import { appStore } from "@store/appStore";
import OrganizationPicker from "@/shared/components/OrganizationPicker/OrganizationPicker";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService.ts";
import { CommandGetPopulationSegments } from "@services/organizations/commands/cmdGetPopulationSegments";
import type { PopulationSegment } from "@models/populationSegment.model";

interface Props {
  open: boolean;
  // Present → edit that definition; absent/null → create a new one.
  definitionId?: number | null;
  onClose: () => void;
  onSaved: (id: number) => void;
  /*
      Hosts that already know the context (the engagement Data File item creates a
      spec FOR a population) pass it in locked. Locking the segment is what keeps
      population_segment_id set — the free-text path is how specs end up with a
      name string and no FK.
  */
  lockedOrganizationId?: number;
  lockedSegment?: PopulationSegment | null;
}

const svc = new IngestDefinitionService();

// Ingest-definition SETTINGS form (org, name, pop segment). This is
// deliberately separate from the field-map editor (column→metric mapping, its
// own page). It never sends `config` on edit, so the legacy config JSON is left
// untouched. Reused for both create and edit.
export default function IngestDefinitionDialog({
  open,
  definitionId,
  onClose,
  onSaved,
  lockedOrganizationId,
  lockedSegment,
}: Props) {
  const appSnap = useSnapshot(appStore);
  const isEdit = definitionId != null;

  const [name, setName] = useState("");
  const [orgId, setOrgId] = useState<number | null>(null);
  const [fileKey, setFileKey] = useState("");
  // Population segment: freeSolo Autocomplete over the org's existing segments.
  // Picking a segment links the FK (popSegmentId) + syncs the legacy name string;
  // typing a new name sets the name only (id cleared), preserving old behavior.
  const [popSegment, setPopSegment] = useState("");
  const [popSegmentId, setPopSegmentId] = useState<number | null>(null);
  const [segments, setSegments] = useState<PopulationSegment[]>([]);
  const [isActive, setIsActive] = useState(true);
  /*
      The PRIMARY spec this one's files hang off (MPN-1564). One field, two meanings:
      files wait for that spec's file to load, AND their rows are restricted to people
      it already loaded. Deliberately not two separate settings — a file marked
      dependent is by definition one whose rows hang off the primary's.
  */
  const [dependsOnId, setDependsOnId] = useState<number | null>(null);
  const [holdOnMissing, setHoldOnMissing] = useState(false);
  const [candidates, setCandidates] = useState<{ id: number; name: string }[]>([]);
  // Org can only change while the definition has no ingest runs (backend-enforced).
  const [hasRuns, setHasRuns] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setName("");
    setOrgId(lockedOrganizationId ?? null);
    setFileKey("");
    setPopSegment(lockedSegment?.name ?? "");
    setPopSegmentId(lockedSegment?.id ?? null);
    setIsActive(true);
    setDependsOnId(null);
    setHasRuns(false);
    setError(null);
  };

  // Load existing values when opening in edit mode; reset when opening for create.
  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      resetForm();
      return;
    }
    setLoading(true);
    setError(null);
    svc
      .getDefinition(definitionId as number)
      .then((d) => {
        setName(d.name ?? "");
        setOrgId(d.organization?.id ?? null);
        setFileKey(d.file_key ?? "");
        // Prefer the linked segment's name; fall back to the legacy free-text string.
        setPopSegmentId(d.population_segment?.id ?? null);
        setPopSegment(d.population_segment?.name ?? d.population_segment_name ?? "");
        setIsActive(d.is_active ?? true);
        setDependsOnId(d.depends_on?.id ?? null);
        setHoldOnMissing(Boolean(d.hold_on_missing_members));
        setHasRuns(Boolean(d.has_ingest_runs));
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load definition"))
      .finally(() => setLoading(false));
  }, [open, definitionId]);

  // Load the org's segments for the picker whenever the chosen org changes.
  useEffect(() => {
    if (!open || orgId == null) {
      setSegments([]);
      return;
    }
    new CommandGetPopulationSegments()
      .run(orgId, 500, 0)
      .then(setSegments)
      .catch(() => setSegments([]));
    // Candidate primaries: the org's other specs. Self is excluded here; deeper cycles
    // are refused by the backend, which is the only place that can see the whole chain.
    svc
      .fetchDefinitions(orgId)
      .then((list: { id: number; name: string }[]) =>
        setCandidates(list.filter((d) => d.id !== definitionId)),
      )
      .catch(() => setCandidates([]));
  }, [open, orgId, definitionId]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const canSave = name.trim().length > 0 && orgId != null && !saving && !loading;

  const handleSave = async () => {
    if (orgId == null || !name.trim()) return;
    const orgs = appSnap.loggedInUser?.permissions?.organizations ?? [];
    const org = orgs.find((o: { id: number }) => o.id === orgId);
    const organization = { id: orgId, name: org?.name ?? "" };
    setSaving(true);
    setError(null);
    try {
      let id: number;
      if (isEdit) {
        // NB: no `config` — the backend preserves it when the field is absent.
        await svc.updateDefinition(definitionId as number, {
          name: name.trim(),
          organization,
          is_active: isActive,
          population_segment_id: popSegmentId ?? undefined,
          population_segment_name: popSegment.trim() || undefined,
          // Always sent, never `undefined`: the backend treats an omitted key as "leave
          // alone" and an explicit null as "unlink", so omitting it would make the
          // picker impossible to clear.
          depends_on_id: dependsOnId,
          hold_on_missing_members: holdOnMissing,
        });
        id = definitionId as number;
      } else {
        const created = await svc.createDefinition({
          name: name.trim(),
          organization,
          // `[{}]` is the model's "no config yet" placeholder ([] fails the blank check).
          config: [{}],
          is_active: isActive,
          population_segment_id: popSegmentId ?? undefined,
          population_segment_name: popSegment.trim() || undefined,
          depends_on_id: dependsOnId,
          hold_on_missing_members: holdOnMissing,
        });
        id = created.id;
      }
      onSaved(id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEdit ? "Edit Ingest Definition" : "New Ingest Definition"}
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            required
            size="small"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. UAA Weekly Roster"
            disabled={loading}
          />
          {lockedOrganizationId == null ? (
            <Box>
              <OrganizationPicker
                label="Organization"
                value={orgId}
                onChange={setOrgId}
                disabled={loading || (isEdit && hasRuns)}
              />
              {isEdit && hasRuns && (
                <Typography sx={{ color: "#8c9097", fontSize: 12, mt: 0.5 }}>
                  Organization is locked — this definition already has ingest runs.
                </Typography>
              )}
            </Box>
          ) : null}
          {fileKey && (
            <TextField
              label="File key"
              size="small"
              value={fileKey}
              InputProps={{ readOnly: true }}
              helperText="Auto-assigned routing token — appears in the file name as [file_key]_[as_of]_[freetext].csv"
              disabled={loading}
            />
          )}
          {/* Always a picker (MPN-1474) — the FK is the source of truth; the old
              locked read-only field left no way to relink a definition. */}
          <Autocomplete
              freeSolo
              size="small"
              options={segments}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name)}
              // Controlled by the displayed name; a matching segment object keeps the FK linked.
              value={segments.find((s) => s.id === popSegmentId) ?? popSegment}
              onChange={(_e, val) => {
                if (val && typeof val !== "string") {
                  setPopSegmentId(val.id);
                  setPopSegment(val.name);
                } else {
                  // Free-typed name (or cleared): no FK, keep the legacy name string.
                  setPopSegmentId(null);
                  setPopSegment(typeof val === "string" ? val : "");
                }
              }}
              onInputChange={(_e, val, reason) => {
                if (reason === "input") {
                  setPopSegment(val);
                  setPopSegmentId(null);
                }
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Population segment"
                  placeholder="e.g. Fall Roster"
                  helperText={
                    orgId == null
                      ? "Choose an organization first to link a segment."
                      : "Pick an existing segment to link it, or type a name."
                  }
                />
              )}
          />
          <Autocomplete
            size="small"
            options={candidates}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={candidates.find((c) => c.id === dependsOnId) ?? null}
            onChange={(_e, val) => setDependsOnId(val?.id ?? null)}
            disabled={loading || orgId == null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Depends on (optional)"
                placeholder="e.g. Student list"
                helperText={
                  dependsOnId == null
                    ? "Leave empty for a standalone file."
                    : popSegmentId == null && !popSegment.trim()
                      ? "Files wait for that file to load. Link a population above to also " +
                        "restrict them to the people it contains."
                      : "Files wait for that file to load, and only load rows for people " +
                        "that file has already added to the population. Everyone else is " +
                        "ignored, not stored."
                }
              />
            )}
          />
          {/* MPN-2121 (MPN-2009 option 1). Off: the file loads and the data issue waits for Ops. */}
          <FormControlLabel
            disabled={loading || dependsOnId != null}
            control={
              <Switch checked={holdOnMissing} onChange={(e) => setHoldOnMissing(e.target.checked)} />
            }
            label={
              <span>
                Hold a file that drops an unusual share of the population
                <Typography component="span" sx={{ display: "block", fontSize: 12, color: "#64748b" }}>
                  {dependsOnId != null
                    ? "Primary files only: a dependent file never decides who is in the population."
                    : "The file waits, unloaded, until the data issue is accepted or corrected. Later files wait behind it."}
                </Typography>
              </span>
            }
          />
          <FormControlLabel
            control={
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={loading} />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          sx={{ textTransform: "none", bgcolor: "#845adf", "&:hover": { bgcolor: "#6e44c8" } }}
        >
          {saving ? "Saving…" : isEdit ? "Save" : "Create & edit field map"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
